import { useContext, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { Footer, PasswordHelper, Wrapper } from 'components'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, Card, Input, toast } from 'keep-react'
import { Eye, EyeSlash } from 'phosphor-react'
import { AuthContext } from 'contexts/AuthContext'
import { authService } from 'api/services/auth'
import { RouteNamesNonAuth } from 'routes/enum'

const validationSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required('Este campo é obrigatório')
    .min(10, 'A password deve ter pelo menos 10 caracteres')
    .matches(/[A-Z]/, 'A password deve conter pelo menos uma letra maiúscula')
    .matches(/[a-z]/, 'A password deve conter pelo menos uma letra minúscula')
    .matches(/[0-9]/, 'A password deve conter pelo menos um número')
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      'A password deve conter pelo menos um símbolo especial',
    ),
  confirmPassword: Yup.string().oneOf(
    [Yup.ref('newPassword')],
    'Passwords têm de ser iguais',
  ),
})

export const ResetPassword = () => {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const token = params.get('token')
  const email = decodeURIComponent(params.get('email') || '')
  const navigate = useNavigate()
  const [isCallingApi, setIsCallingApi] = useState<boolean>(false)
  const { isSignedIn } = useContext(AuthContext)
  const [newPasswordFieldType, setNewPasswordFieldType] = useState<
    'password' | 'text'
  >('password')
  const [confirmPasswordFieldType, setConfirmPasswordFieldType] = useState<
    'password' | 'text'
  >('password')
  const formik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async values => {
      setIsCallingApi(true)

      if (!token || !email) {
        toast.error(
          'Pedido inválido. Por favor tente novamente ou contacte a Innovarisk.',
        )
        setIsCallingApi(false)
      }

      try {
        await authService.resetPassword({
          email,
          newPassword: values.newPassword,
          resetToken: token || '',
        })
        toast.success(
          'Password alterada com sucesso. Já pode iniciar sessão com a sua nova password.',
        )
        navigate(RouteNamesNonAuth.LOGIN)
      } catch (error) {
        toast.error(
          'Ocorreu um erro ao tentar mudar a password. Por favor tente novamente ou contacte a Innovarisk.',
        )
      } finally {
        setIsCallingApi(false)
      }
    },
  })

  if (isSignedIn) {
    navigate('/')
  }

  return (
    <div
      className="w-screen h-screen bg-cover bg-no-repeat bg-center flex items-center"
      style={{
        backgroundImage: `url(images/dark_broker.jpg)`,
      }}
    >
      <Wrapper>
        <div className="flex flex-col items-center m-auto gap-x-4 gap-y-12 max-w-[700px] h-full">
          <h1 className="text-white uppercase font-semibold text-2xl text-center tracking-wider">
            Seguros à velocidade de um foguetão
          </h1>
          <div className="grid grid-cols-2 gap-12 items-start w-full">
            <Card className="w-full bg-white/70 p-4 rounded-xl max-w-none h-full">
              <h2 className="font-light text-heading-6 text-primary-400 pb-4 uppercase">
                Mudar a password
              </h2>
              <form onSubmit={formik.handleSubmit}>
                <div className="space-y-5">
                  <Input
                    type="email"
                    name="email"
                    onChange={formik.handleChange}
                    placeholder="Email"
                    className="px-2"
                    disabled
                    value={email}
                  />
                  <div className="relative">
                    <Input
                      type={newPasswordFieldType}
                      name="newPassword"
                      onChange={formik.handleChange}
                      placeholder="Nova password"
                      className="px-2"
                    />
                    <Button
                      type="button"
                      shape="icon"
                      color="secondary"
                      className="absolute top-1/2 right-3 -translate-y-1/2 p-0 text-metal-900 bg-transparent hover:bg-transparent"
                      onClick={() => {
                        if (newPasswordFieldType === 'password') {
                          setNewPasswordFieldType('text')
                        } else {
                          setNewPasswordFieldType('password')
                        }
                      }}
                    >
                      {newPasswordFieldType === 'password' ? (
                        <Eye size={16} />
                      ) : (
                        <EyeSlash size={16} />
                      )}
                    </Button>
                  </div>

                  <div className="relative">
                    <Input
                      type={confirmPasswordFieldType}
                      name="confirmPassword"
                      onChange={formik.handleChange}
                      placeholder="Confirmar password"
                      className="px-2"
                    />
                    <Button
                      type="button"
                      shape="icon"
                      color="secondary"
                      className="absolute top-1/2 right-3 -translate-y-1/2 p-0 text-metal-900 bg-transparent hover:bg-transparent"
                      onClick={() => {
                        if (confirmPasswordFieldType === 'password') {
                          setConfirmPasswordFieldType('text')
                        } else {
                          setConfirmPasswordFieldType('password')
                        }
                      }}
                    >
                      {confirmPasswordFieldType === 'password' ? (
                        <Eye size={16} />
                      ) : (
                        <EyeSlash size={16} />
                      )}
                    </Button>
                  </div>

                  <Button
                    className="w-full"
                    type="submit"
                    disabled={isCallingApi}
                  >
                    Reset Password
                  </Button>
                </div>
              </form>
            </Card>

            <Card className="w-full bg-white/70 p-4 rounded-xl max-w-none h-full">
              <PasswordHelper password={formik.values.newPassword} />
            </Card>
          </div>
        </div>
      </Wrapper>
      <Footer />
    </div>
  )
}
